import { axiosClient } from '@/libs/axios-client';

const API_BASE = '/di-reports';

// ============================================
// NBS form DI1 (foreign direct investment), quarterly per company
// ============================================

const fileNameFrom = (disposition = '', fallback) => {
  const utf8 = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8) return decodeURIComponent(utf8[1]);
  const plain = disposition.match(/filename="?([^";]+)"?/i);
  return plain ? plain[1] : fallback;
};

/**
 * @param {Object} period - { year, quarter } (quarter 5 = final report)
 * @returns {Promise<Object>} { year, quarter, periodEnd, rate, rateDate, items }
 */
export const getQuarter = async (period) => {
  const response = await axiosClient.get(API_BASE, { params: period });
  return response.data.data;
};

/** One company's DI1 for the period: profile, report, Table 4 and what is missing. */
export const getCompany = async (companyId, period) => {
  const response = await axiosClient.get(`${API_BASE}/${companyId}`, {
    params: period,
  });
  return response.data.data;
};

export const saveProfile = async (companyId, body) => {
  const response = await axiosClient.put(
    `${API_BASE}/${companyId}/profile`,
    body
  );
  return response.data.data;
};

/** Reads members and capital from APR again (takes up to a minute). */
export const refreshFromApr = async (companyId) => {
  const response = await axiosClient.post(
    `${API_BASE}/${companyId}/apr`,
    null,
    { timeout: 180000 }
  );
  return response.data.data;
};

export const saveReport = async (companyId, period, body) => {
  const response = await axiosClient.put(
    `${API_BASE}/${companyId}/report`,
    body,
    { params: period }
  );
  return response.data.data;
};

export const setSent = async (companyId, period, sent) => {
  const response = await axiosClient.post(
    `${API_BASE}/${companyId}/sent`,
    { sent },
    { params: period }
  );
  return response.data.data;
};

/** @returns {Promise<Object>} { blob, fileName } */
export const downloadFile = async (companyId, period) => {
  const response = await axiosClient.get(`${API_BASE}/${companyId}/file`, {
    params: period,
    responseType: 'blob',
    timeout: 120000,
  });
  return {
    blob: response.data,
    fileName: fileNameFrom(response.headers['content-disposition'], 'DI1.xls'),
  };
};

/**
 * Every ready form of the period in one zip.
 *
 * @returns {Promise<Object>} { blob, fileName, count, skipped }
 */
export const downloadZip = async (period, companyIds = null) => {
  const response = await axiosClient.get(`${API_BASE}/zip`, {
    params: {
      ...period,
      companies: companyIds?.length ? companyIds.join(',') : undefined,
    },
    responseType: 'blob',
    timeout: 600000,
  });
  return {
    blob: response.data,
    fileName: fileNameFrom(response.headers['content-disposition'], 'DI1.zip'),
    count: Number(response.headers['x-di-count']) || 0,
    skipped: Number(response.headers['x-di-skipped']) || 0,
  };
};

export const getSettings = async () => {
  const response = await axiosClient.get(`${API_BASE}/settings`);
  return response.data.data;
};

export const saveSettings = async (body) => {
  const response = await axiosClient.put(`${API_BASE}/settings`, body);
  return response.data.data;
};

export const getCountries = async () => {
  const response = await axiosClient.get(`${API_BASE}/codes/countries`);
  return response.data.data;
};

export const searchSettlements = async (q) => {
  const response = await axiosClient.get(`${API_BASE}/codes/settlements`, {
    params: { q },
  });
  return response.data.data;
};

/** Companies in the caller's portfolio that are not on the DI1 list yet. */
export const getCandidates = async (q) => {
  const response = await axiosClient.get(`${API_BASE}/candidates`, {
    params: { q },
  });
  return response.data.data;
};
